import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryConfirmationPage = () => {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const inquiryId = searchParams.get('id');
  const emailSent = searchParams.get('email_sent') === 'true';
  const [inquiryDetails, setInquiryDetails] = useState(null);

  const fetchInquiryDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inquiry/${inquiryId}`);
      setInquiryDetails(response.data);
    } catch (error) {
      console.error('Error fetching inquiry:', error);
    }
  }, [inquiryId]);

  useEffect(() => {
    if (inquiryId) {
      fetchInquiryDetails();
    }
  }, [inquiryId, fetchInquiryDetails]);

  return (
    <div data-testid="inquiry-confirmation-page" className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-[#F5F1EC]">
      <div className="px-6 md:px-12 max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="success-message"
        >
          <CheckCircle className="w-24 h-24 text-[#E92A3E] mx-auto mb-8" />
          
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#111] mb-6 leading-tight whitespace-pre-line">
            {t('inquiry.confirmTitle')}
          </h1>
          
          <p className="text-lg md:text-xl text-[#555] mb-8 leading-relaxed">
            {t('inquiry.confirmDesc')}
          </p>

          {inquiryDetails && (
            <div className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 rounded-sm mb-10 text-left">
              <h2 className="font-heading text-xl font-bold text-[#111] mb-6">{t('inquiry.whatHappensNext')}</h2>
              <div className="space-y-4 text-[#555]">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E92A3E]/10 border border-[#E92A3E]/30 flex items-center justify-center flex-shrink-0 text-[#E92A3E] font-semibold text-sm">1</div>
                  <div>
                    <p className="text-[#111] font-medium mb-1">{t('inquiry.confirmStep1Title')}</p>
                    <p className="text-sm">{t('inquiry.confirmStep1Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E92A3E]/10 border border-[#E92A3E]/30 flex items-center justify-center flex-shrink-0 text-[#E92A3E] font-semibold text-sm">2</div>
                  <div>
                    <p className="text-[#111] font-medium mb-1">{t('inquiry.confirmStep2Title')}</p>
                    <p className="text-sm">{t('inquiry.confirmStep2Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E92A3E]/10 border border-[#E92A3E]/30 flex items-center justify-center flex-shrink-0 text-[#E92A3E] font-semibold text-sm">3</div>
                  <div>
                    <p className="text-[#111] font-medium mb-1">{t('inquiry.confirmStep3Title')}</p>
                    <p className="text-sm">{t('inquiry.confirmStep3Desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-[#F5F1EC] border border-[#D9D3CC] p-8 rounded-sm mb-10">
            {emailSent ? (
              <p className="text-[#111] mb-6" data-testid="email-sent-message">
                {t('inquiry.emailSent')} <strong>{inquiryDetails?.email}</strong>
              </p>
            ) : (
              <p className="text-[#555] mb-6" data-testid="no-email-message">
                {t('inquiry.noEmail')}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
              <a
                href="mailto:studio@redhouse.co"
                data-testid="contact-email-link"
                className="flex items-center gap-2 text-[#E92A3E] hover:text-[#D0212F] transition-colors"
              >
                <Mail size={16} />
                studio@redhouse.co
              </a>
              <a
                href="tel:+33651923617"
                data-testid="contact-phone-link"
                className="flex items-center gap-2 text-[#E92A3E] hover:text-[#D0212F] transition-colors"
              >
                <Phone size={16} />
                +33 6 51 92 36 17
              </a>
            </div>
          </div>

          <Link
            to="/"
            data-testid="return-home-button"
            className="inline-block btn-primary rounded-sm font-semibold px-10 py-4 uppercase tracking-wide text-sm"
          >
            {t('inquiry.returnHome')}
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default InquiryConfirmationPage;
